import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  CreatePersonAddressCommand,
  UpdatePersonAddressCommand
} from '../../../models/Entities/Person/Address/AddressModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as ownFunctions from './PersonAddress.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonAddressFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [country, setCountry] = useState<string>('');

  useEffect(() => {
    if (props.address) {
      setStreet(props.address.street || '');
      setCity(props.address.city || '');
      setPostalCode(props.address.postalCode || '');
      setCountry(props.address.country || '');
    } else {
      resetForm();
    }
  }, [props.address, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setStreet('');
    setCity('');
    setPostalCode('');
    setCountry('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!street.trim()) {
      errors.push(t('persons.addresses.form.validation.street', { defaultValue: 'La calle es obligatoria' }));
    }

    if (!city.trim()) {
      errors.push(t('persons.addresses.form.validation.city', { defaultValue: 'La ciudad es obligatoria' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: CreatePersonAddressCommand = {
      personId: props.PersonId,
      street: street.trim(),
      city: city.trim(),
      postalCode: postalCode.trim() || null,
      country: country.trim() || null
    };

    if (props.address) {
      props.UpdateAddress({ ...payload, id: props.address.id } as UpdatePersonAddressCommand);
    } else {
      props.CreateAddress(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.address
            ? t('persons.addresses.form.editTitle', { defaultValue: 'Editar domicilio' })
            : t('persons.addresses.form.addTitle', { defaultValue: 'Nuevo domicilio' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.addresses.form.street', { defaultValue: 'Calle y número' })}
            required
            validationMessage={
              submitted && !street.trim()
                ? t('persons.addresses.form.validation.street', { defaultValue: 'La calle es obligatoria' })
                : undefined
            }
            validationState={submitted && !street.trim() ? 'error' : undefined}
          >
            <Input value={street} onChange={(_, data) => setStreet(data.value || '')} />
          </Field>

          <Field
            label={t('persons.addresses.form.city', { defaultValue: 'Ciudad' })}
            required
            validationMessage={
              submitted && !city.trim()
                ? t('persons.addresses.form.validation.city', { defaultValue: 'La ciudad es obligatoria' })
                : undefined
            }
            validationState={submitted && !city.trim() ? 'error' : undefined}
          >
            <Input value={city} onChange={(_, data) => setCity(data.value || '')} />
          </Field>

          <Field label={t('persons.addresses.form.postalCode', { defaultValue: 'Código postal' })}>
            <Input value={postalCode} onChange={(_, data) => setPostalCode(data.value || '')} />
          </Field>

          <Field label={t('persons.addresses.form.country', { defaultValue: 'País' })}>
            <Input value={country} onChange={(_, data) => setCountry(data.value || '')} />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonAddressFormComponent as any);
